import { Icon } from '@aibees/shared/components';
import { KeyCodes } from '@aibees/shared/definition';
import { classnames } from '@aibees/shared/utils';
import { Box, TextFieldProps, Tooltip } from '@mui/material';
import { memo } from 'react';
import { TOOLTIP_SLOT_PROPS } from '../../constant';
import { useSuggestionSearchBarContext } from '../../context';
import { useErrorTooltipDisabled } from '../../hooks/use-error-tooltip-disabled';
import { useSearchInputPlaceholder } from '../../hooks/use-search-input-placeholder';
import { combineValuesAndSearchKey } from '../../utils';
import { ScrollableArea } from '../scrollable-area';
import { TagWrapper } from '../tag-wrapper';
import * as Styled from './styles';
import * as SX from './sx';
import type { InputWrapperProps as Props } from './types';

export const InputWrapper = memo(({ className, limitErrorShortMsg }: Props) => {
  const {
    focused,
    setAnchorEl,
    placeholderText,
    selectedItems,
    multiple,
    getTagProps,
    getInputProps,
    onDelete,
    onClickSearchButton,
    searchKey,
    limitReached,
    searchIcon,
    renderMode,
    onRemoveLookalike,
    deleteItemWithBackspace,
  } = useSuggestionSearchBarContext();

  const placeholder = useSearchInputPlaceholder(
    placeholderText,
    limitReached,
    selectedItems
  );

  const handleKeyDown: TextFieldProps['onKeyDown'] = (event) => {
    if (event.key === KeyCodes.enter && selectedItems.length > 0) {
      const v = combineValuesAndSearchKey<any>(selectedItems, searchKey);
      if (v && onClickSearchButton) onClickSearchButton(v);
    }
    if (
      deleteItemWithBackspace &&
      searchKey?.length === undefined &&
      event.key === KeyCodes.backspace &&
      selectedItems.length > 0
    ) {
      const lastSelectedItem = selectedItems.at(-1);
      if (lastSelectedItem) {
        onRemoveLookalike(lastSelectedItem);
      }
    }
  };

  const tooltipDisabled = useErrorTooltipDisabled(
    selectedItems,
    limitErrorShortMsg
  );

  const dialogMode = renderMode === 'dialog';

  return (
    <Styled.Wrapper
      className={classnames('InputWrapper', className)}
      sx={SX.root}
    >
      <Styled.InputWrapper
        ref={setAnchorEl}
        className={classnames('InputWrapper', { focused })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            flexWrap: 'wrap',
          }}
        >
          {selectedItems?.length === 0 && searchIcon && (
            <Icon name={searchIcon} color="grey.500" size={18} />
          )}
          <ScrollableArea>
            <TagWrapper
              multiple={multiple}
              onDelete={onDelete}
              value={selectedItems}
              getTagProps={getTagProps}
            />

            <Tooltip
              title={limitErrorShortMsg}
              placement="top-end"
              disableHoverListener={tooltipDisabled}
              disableTouchListener={tooltipDisabled}
              disableFocusListener={tooltipDisabled}
              slotProps={TOOLTIP_SLOT_PROPS}
            >
              <Styled.SearchInput
                {...getInputProps()}
                value={searchKey ?? ''}
                placeholder={placeholder}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={limitReached}
              />
            </Tooltip>
          </ScrollableArea>
        </Box>
      </Styled.InputWrapper>
    </Styled.Wrapper>
  );
});

InputWrapper.displayName = 'InputWrapper';
